import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query"
import { routes } from "@nova-ai/shared"
import type { GalleryCanvasRenderRequest } from "@nova-ai/gallery-canvas"

import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

export type VisualDirectionAssetKind =
  | "font"
  | "logo"
  | "icon"
  | "badge"
  | "decoration"
  | "style_image"
  | "model_reference"

export type VisualDirectionAsset = {
  id: string
  name: string
  kind: VisualDirectionAssetKind
  use: "direct_composition" | "generation_reference"
  mediaType: string
  byteSize: number
  width: number | null
  height: number | null
  byteHash: string
  uploadedBy: string
  uploadedAt: string
}

export type VisualDirectionPalette = { id: string; name: string; colors: string[] }

export type VisualDirectionDraft = {
  directionId: string
  version: number
  compatibleBrandFamilies: string[]
  palettes: VisualDirectionPalette[]
  typographyDefaults: { headingFontAssetId: string | null; bodyFontAssetId: string | null }
  compositionDefaults: {
    paletteId: string | null
    logoAssetId: string | null
    foreground: string | null
    background: string | null
    accent: string | null
  }
  generationGuidance: string | null
  updatedBy: string
  updatedAt: string
  assets: VisualDirectionAsset[]
}

export type VisualDirectionVersion = Omit<
  VisualDirectionDraft,
  "directionId" | "updatedBy" | "updatedAt"
> & {
  id: string
  directionId: string
  name: string
  publishedBy: string
  publishedAt: string
  archivedBy: string | null
  archivedAt: string | null
}

type VisualDirectionVersionSummary = Omit<VisualDirectionVersion, "assets">

export type VisualDirection = {
  direction: {
    id: string
    name: string
    createdBy: string
    createdAt: string
    archivedBy: string | null
    archivedAt: string | null
    updatedAt: string
  }
  draft: VisualDirectionDraft
  versions: VisualDirectionVersion[]
  nextVersionCursor: number | null
}

type ProductVisualDirectionState = {
  configSku: string
  brandFamily: string
  selection: {
    selection: {
      configSku: string
      versionId: string | null
      selectedBy: string
      selectedAt: string
    }
    version: VisualDirectionVersionSummary | null
    direction: VisualDirection["direction"] | null
  } | null
  suggestion: {
    default: { brandFamily: string; versionId: string; setBy: string; setAt: string }
    version: VisualDirectionVersionSummary
    direction: VisualDirection["direction"]
  } | null
  compatibleVersions: {
    version: VisualDirectionVersionSummary
    direction: VisualDirection["direction"]
  }[]
}

type VisualDirectionListItem = Pick<VisualDirection, "direction"> & {
  draft: Omit<VisualDirectionDraft, "assets">
}

export type VisualDirectionProposal = {
  proposal: {
    id: string
    directionId: string
    baseDraftVersion: number
    inputAssets: Pick<
      VisualDirectionAsset,
      "id" | "name" | "kind" | "use" | "mediaType" | "byteHash" | "byteSize"
    >[]
    provider: string
    model: string
    settings: Record<string, unknown>
    promptVersion: string
    providerRequestId: string
    requestedBy: string
    status: "pending" | "succeeded" | "failed"
    usage: Record<string, unknown> | null
    responseProvenance: Record<string, unknown> | null
    output: {
      name: string
      compatibleBrandFamilies: string[]
      palettes: VisualDirectionPalette[]
      typographyDefaults: VisualDirectionDraft["typographyDefaults"]
      compositionDefaults: VisualDirectionDraft["compositionDefaults"]
      generationGuidance: string | null
    } | null
    failureCode: string | null
    failureMessage: string | null
    createdAt: string
    completedAt: string | null
  }
  application: {
    proposalId: string
    baseDraftVersion: number
    resultingDraftVersion: number
    appliedBy: string
    appliedAt: string
  } | null
  lateProviderEvidence: {
    proposalId: string
    providerRequestId: string
    providerOutcome: "succeeded" | "failed"
    usage: Record<string, unknown>
    responseId: string | null
    responseProvenance: Record<string, unknown>
    failureCode: string | null
    failureMessage: string | null
    recordedAt: string
  } | null
}

function resultValue<Value>(result: Awaited<ReturnType<typeof apiFetch<Value>>>): Value {
  if (!result.ok) {
    throw new Error(result.error.message)
  }
  return result.value
}

export function useVisualDirections(includeArchived = false) {
  const query = useInfiniteQuery({
    queryKey: [...queryKeys.visualDirections, includeArchived],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) =>
      resultValue(
        await apiFetch<{ directions: VisualDirectionListItem[]; nextCursor: string | null }>(
          `${routes.visualDirections.list()}?includeArchived=${includeArchived}&limit=25${pageParam ? `&beforeId=${encodeURIComponent(pageParam)}` : ""}`
        )
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  })
  return {
    ...query,
    data: query.data
      ? {
          directions: query.data.pages.flatMap((page) => page.directions),
          nextCursor: query.data.pages.at(-1)?.nextCursor ?? null,
        }
      : undefined,
  }
}

export function useVisualDirection(directionId: string | null) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.visualDirection(directionId ?? "none"),
    enabled: directionId !== null,
    initialPageParam: null as number | null,
    queryFn: async ({ pageParam }) =>
      resultValue(
        await apiFetch<VisualDirection>(
          `${routes.visualDirections.detail({ directionId: directionId ?? "" })}?versionLimit=10${pageParam ? `&beforeVersion=${pageParam}` : ""}`
        )
      ),
    getNextPageParam: (lastPage) => lastPage.nextVersionCursor ?? undefined,
  })
  const firstPage = query.data?.pages[0]
  return {
    ...query,
    data: firstPage
      ? {
          ...firstPage,
          versions: query.data?.pages.flatMap((page) => page.versions) ?? [],
          nextVersionCursor: query.data?.pages.at(-1)?.nextVersionCursor ?? null,
        }
      : undefined,
  }
}

export function useVisualDirectionProposals(directionId: string | null) {
  return useQuery({
    queryKey: queryKeys.visualDirectionProposals(directionId ?? "none"),
    enabled: directionId !== null,
    queryFn: async () =>
      resultValue(
        await apiFetch<{ proposals: VisualDirectionProposal[] }>(
          `${routes.visualDirections.proposals({ directionId: directionId ?? "" })}?limit=10`
        )
      ),
    refetchInterval: (query) =>
      query.state.data?.proposals.some(({ proposal }) => proposal.status === "pending")
        ? 2_000
        : false,
  })
}

export function useVisualDirectionFontGlyphCoverage(assetId: string | null) {
  return useQuery({
    queryKey: queryKeys.visualDirectionFontCoverage(assetId ?? "none"),
    enabled: assetId !== null,
    queryFn: async () =>
      resultValue(
        await apiFetch<{ assetId: string; sample: string; missingCodePoints: number[] }>(
          routes.visualDirections.assetGlyphCoverage({ assetId: assetId ?? "" })
        )
      ),
  })
}

export function useProductVisualDirection(configSku: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.productVisualDirection(configSku),
    enabled: enabled && configSku.length > 0,
    queryFn: async () => {
      const state = resultValue(
        await apiFetch<ProductVisualDirectionState>(
          routes.visualDirections.productSelection({ configSku })
        )
      )
      return { state, compatibleVersions: state.compatibleVersions }
    },
  })
}

export function useVisualDirectionMoodboardSample(
  directionId: string,
  configSku: string,
  proposalId?: string
) {
  return useQuery({
    queryKey: ["visual-direction-moodboard-sample", directionId, configSku, proposalId ?? null],
    enabled: directionId.length > 0 && configSku.trim().length > 0,
    queryFn: async () =>
      resultValue(
        await apiFetch<GalleryCanvasRenderRequest>(
          `${routes.visualDirections.moodboardSample({ directionId })}?configSku=${encodeURIComponent(configSku.trim())}${proposalId ? `&proposalId=${encodeURIComponent(proposalId)}` : ""}`
        )
      ),
  })
}

export function useSetProductVisualDirection(configSku: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (versionId: string | null) =>
      resultValue(
        await apiFetch(routes.visualDirections.productSelection({ configSku }), {
          method: "PUT",
          body: { versionId },
        })
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.productVisualDirection(configSku) }),
        queryClient.invalidateQueries({ queryKey: ["gallery-studio", configSku] }),
      ])
    },
  })
}

function useRefreshingVisualDirectionMutation<Variables, Value>(
  request: (variables: Variables) => Promise<Value>,
  directionId: (variables: Variables) => string | undefined
): UseMutationResult<Value, Error, Variables> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: async (_value, variables) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.visualDirections })
      const selectedDirectionId = directionId(variables)
      if (selectedDirectionId) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: queryKeys.visualDirection(selectedDirectionId),
          }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.visualDirectionProposals(selectedDirectionId),
          }),
        ])
      }
    },
    onSettled: async (_value, _error, variables) => {
      const selectedDirectionId = directionId(variables)
      if (selectedDirectionId) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.visualDirectionProposals(selectedDirectionId),
        })
      }
    },
  })
}

export function useVisualDirectionMutations() {
  const create = useRefreshingVisualDirectionMutation(
    async (name: string) =>
      resultValue(
        await apiFetch<VisualDirection>(routes.visualDirections.list(), {
          method: "POST",
          body: { name },
        })
      ),
    () => undefined
  )
  const update = useRefreshingVisualDirectionMutation(
    async ({
      directionId,
      ...body
    }: {
      directionId: string
      expectedVersion: number
      name: string
      compatibleBrandFamilies: string[]
      palettes: VisualDirectionPalette[]
      typographyDefaults: VisualDirectionDraft["typographyDefaults"]
      compositionDefaults: VisualDirectionDraft["compositionDefaults"]
      generationGuidance: string | null
    }) =>
      resultValue(
        await apiFetch(routes.visualDirections.draft({ directionId }), { method: "PATCH", body })
      ),
    (variables) => variables.directionId
  )
  const upload = useRefreshingVisualDirectionMutation(
    async ({
      directionId,
      ...body
    }: {
      directionId: string
      expectedVersion: number
      name: string
      kind: VisualDirectionAssetKind
      mediaType: string
      bytesBase64: string
    }) =>
      resultValue(
        await apiFetch(routes.visualDirections.assets({ directionId }), { method: "POST", body })
      ),
    (variables) => variables.directionId
  )
  const removeAsset = useRefreshingVisualDirectionMutation(
    async ({
      directionId,
      assetId,
      expectedVersion,
    }: {
      directionId: string
      assetId: string
      expectedVersion: number
    }) =>
      resultValue(
        await apiFetch(routes.visualDirections.asset({ directionId, assetId }), {
          method: "DELETE",
          body: { expectedVersion },
        })
      ),
    (variables) => variables.directionId
  )
  const publish = useRefreshingVisualDirectionMutation(
    async ({ directionId, expectedVersion }: { directionId: string; expectedVersion: number }) =>
      resultValue(
        await apiFetch(routes.visualDirections.publish({ directionId }), {
          method: "POST",
          body: { expectedVersion, confirm: true },
        })
      ),
    (variables) => variables.directionId
  )
  const generateProposal = useRefreshingVisualDirectionMutation(
    async ({ directionId, expectedVersion }: { directionId: string; expectedVersion: number }) =>
      resultValue(
        await apiFetch<VisualDirectionProposal>(
          routes.visualDirections.proposals({ directionId }),
          {
            method: "POST",
            body: { expectedVersion },
          }
        )
      ),
    (variables) => variables.directionId
  )
  const applyProposal = useRefreshingVisualDirectionMutation(
    async ({ directionId, proposalId }: { directionId: string; proposalId: string }) =>
      resultValue(
        await apiFetch(routes.visualDirections.applyProposal({ directionId, proposalId }), {
          method: "POST",
          body: { confirm: true },
        })
      ),
    (variables) => variables.directionId
  )
  const abandonProposal = useRefreshingVisualDirectionMutation(
    async ({ directionId, proposalId }: { directionId: string; proposalId: string }) =>
      resultValue(
        await apiFetch(routes.visualDirections.abandonProposal({ directionId, proposalId }), {
          method: "POST",
          body: { confirm: true },
        })
      ),
    (variables) => variables.directionId
  )
  const archive = useRefreshingVisualDirectionMutation(
    async (directionId: string) =>
      resultValue(
        await apiFetch(routes.visualDirections.archive({ directionId }), {
          method: "POST",
          body: { confirm: true },
        })
      ),
    (directionId) => directionId
  )
  const archiveVersion = useRefreshingVisualDirectionMutation(
    async ({ directionId, versionId }: { directionId: string; versionId: string }) =>
      resultValue(
        await apiFetch(routes.visualDirections.archiveVersion({ directionId, versionId }), {
          method: "POST",
          body: { confirm: true },
        })
      ),
    (variables) => variables.directionId
  )
  const setDefault = useRefreshingVisualDirectionMutation(
    async ({
      brandFamily,
      versionId,
    }: {
      directionId: string
      brandFamily: string
      versionId: string | null
    }) =>
      resultValue(
        await apiFetch(routes.visualDirections.brandDefault({ brandFamily }), {
          method: "PUT",
          body: { versionId },
        })
      ),
    (variables) => variables.directionId
  )

  return {
    create,
    update,
    upload,
    removeAsset,
    generateProposal,
    abandonProposal,
    applyProposal,
    publish,
    archive,
    archiveVersion,
    setDefault,
  }
}
